import { useEffect, useRef, useState } from "react";
import { Plus, Edit, Trash2, Upload, ArrowUp, ArrowDown, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PRICE_TYPES = [
  { key: "fixed", label: "Fixed price" },
  { key: "approx", label: "Approx" },
  { key: "on_request", label: "Price on Request" },
];

export default function ExperiencesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.get("/admin/experiences")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this experience?")) return;
    await api.delete(`/admin/experiences/${id}`);
    toast.success("Experience deleted");
    reload();
  };

  const move = async (item, delta) => {
    const idx = items.findIndex((x) => x.id === item.id);
    const target = items[idx + delta];
    if (!target) return;
    await Promise.all([
      api.patch(`/admin/experiences/${item.id}`, { ...item, display_order: target.display_order }),
      api.patch(`/admin/experiences/${target.id}`, { ...target, display_order: item.display_order }),
    ]);
    reload();
  };

  const toggle = async (item) => {
    await api.patch(`/admin/experiences/${item.id}`, { ...item, enabled: !item.enabled });
    reload();
  };

  return (
    <div data-testid="experiences-admin">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-rose-500">Add-ons</p>
          <h2 className="font-display text-3xl text-charcoal">Experiences</h2>
          <p className="text-sm text-charcoal/60 mt-1">Extra add-ons customers can attach to any celebration plan.</p>
        </div>
        <button onClick={() => setEditing({})}
          className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/40 flex items-center gap-2"
          style={{ background: "linear-gradient(135deg, #E11D48 0%, #A855F7 100%)" }}
          data-testid="new-experience">
          <Plus className="w-4 h-4" strokeWidth={2.5} /> New experience
        </button>
      </div>

      {loading ? (
        <p className="text-charcoal/60">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-charcoal/60">No experiences yet.</p>
      ) : (
        <div className="grid gap-3">
          {items.map((it, i) => (
            <div key={it.id}
              className={`card-surface p-4 flex items-center gap-4 ${it.enabled ? "" : "opacity-60"}`}
              data-testid={`experience-row-${it.id}`}>
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-linen-2">
                {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display text-lg text-charcoal">{it.name}</p>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-linen-2 text-charcoal/60 border border-warm">
                    {PRICE_TYPES.find((p) => p.key === it.price_type)?.label || it.price_type}
                  </span>
                  {!it.enabled && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-charcoal/10 text-charcoal/60">Disabled</span>
                  )}
                </div>
                <p className="text-sm text-charcoal/60 mt-0.5 line-clamp-1">{it.description}</p>
                <p className="text-xs text-charcoal/50 mt-1">
                  Order #{it.display_order} · {it.price_type === "on_request" ? "Price on request" : `₹${it.price}`}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => move(it, -1)} disabled={i === 0}
                  className="p-2 rounded-lg hover:bg-linen-2 disabled:opacity-30" title="Move up">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => move(it, 1)} disabled={i === items.length - 1}
                  className="p-2 rounded-lg hover:bg-linen-2 disabled:opacity-30" title="Move down">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button onClick={() => toggle(it)}
                  className={`p-2 rounded-lg hover:bg-linen-2 ${it.enabled ? "text-emerald-600" : "text-charcoal/40"}`}
                  title={it.enabled ? "Enabled" : "Disabled"}
                  data-testid={`toggle-experience-${it.id}`}>
                  {it.enabled ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                </button>
                <button onClick={() => setEditing(it)}
                  className="p-2 rounded-lg hover:bg-linen-2"
                  data-testid={`edit-experience-${it.id}`}>
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => remove(it.id)}
                  className="p-2 rounded-lg hover:bg-rose-50 text-rose-600"
                  data-testid={`delete-experience-${it.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <ExperienceDialog
          initial={editing.id ? editing : null}
          maxOrder={items.length}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function ExperienceDialog({ initial, maxOrder, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    description: initial?.description || "",
    image_url: initial?.image_url || "",
    price: initial?.price ?? 0,
    price_type: initial?.price_type || "fixed",
    enabled: initial?.enabled ?? true,
    display_order: initial?.display_order ?? (maxOrder + 1),
    tagline: initial?.tagline || "",
    inclusions: (initial?.inclusions || []).join("\n"),
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload?folder=experiences", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseInt(form.price, 10) || 0,
        display_order: parseInt(form.display_order, 10) || 0,
        inclusions: form.inclusions.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (initial?.id) {
        await api.patch(`/admin/experiences/${initial.id}`, payload);
        toast.success("Experience updated");
      } else {
        await api.post("/admin/experiences", payload);
        toast.success("Experience created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm"
      onClick={onClose} data-testid="experience-dialog">
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
        className="bg-white rounded-3xl max-w-lg w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-warm flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-rose-500">
              {initial ? "Edit" : "New"}
            </p>
            <h3 className="font-display text-2xl text-charcoal">Experience</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-linen-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">Image</label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url} alt="" className="w-24 h-24 rounded-xl object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-linen-2" />
              )}
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="pill-btn pill-btn-secondary text-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="experience-upload-btn">
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload image"}
                </button>
                <input type="url" placeholder="…or paste image URL"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="mt-2 w-full rounded-xl bg-white border border-warm px-3 py-2 text-xs" />
              </div>
            </div>
          </div>

          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="experience-name" />
          </Field>

          <Field label="Description">
            <textarea rows={3} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400 resize-none" />
          </Field>

          <Field label="Tagline (shown on the experience page)">
            <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="An evening under the stars…"
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
              data-testid="experience-tagline" />
          </Field>

          <Field label="What's included (one per line)">
            <textarea rows={4} value={form.inclusions}
              onChange={(e) => setForm({ ...form, inclusions: e.target.value })}
              placeholder={"Private table setup\nCandles & flowers\n3-course menu"}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400 resize-none"
              data-testid="experience-inclusions" />
          </Field>

          <Field label="Price type">
            <div className="grid grid-cols-3 gap-2">
              {PRICE_TYPES.map((t) => (
                <button key={t.key} type="button" onClick={() => setForm({ ...form, price_type: t.key })}
                  className={`rounded-xl px-3 py-2 text-xs font-semibold border-2 transition-all ${
                    form.price_type === t.key
                      ? "border-rose-500 bg-rose-50 text-charcoal"
                      : "border-warm bg-white text-charcoal/70 hover:border-charcoal/30"
                  }`}
                  data-testid={`experience-pt-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </Field>

          {form.price_type !== "on_request" && (
            <Field label="Price (₹)">
              <input type="number" min="0" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
                data-testid="experience-price" />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Display order">
              <input type="number" min="0" value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" />
            </Field>
            <label className="flex items-end gap-2 mb-3 cursor-pointer">
              <input type="checkbox" checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="w-4 h-4 rounded accent-rose-500"
                data-testid="experience-enabled" />
              <span className="text-sm text-charcoal/80">Enabled</span>
            </label>
          </div>
        </div>

        <div className="p-6 border-t border-warm flex justify-end gap-2">
          <button type="button" onClick={onClose} className="pill-btn pill-btn-secondary text-sm">Cancel</button>
          <button type="submit" disabled={saving}
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #E11D48 0%, #A855F7 100%)" }}
            data-testid="experience-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">{label}</label>
    {children}
  </div>
);
